import React from 'react';
import { BookOpen, Users, BarChart3, Shield, Smartphone, FileText, UserCheck, Bell, Settings } from 'lucide-react';
import { Link } from 'react-router-dom';

const Features: React.FC = () => {
  const features = [
    {
      icon: BarChart3,
      title: 'Dashboard & Analytics',
      description: 'Comprehensive analytics dashboard providing real-time insights into student performance, course engagement, and institutional metrics.',
      details: [
        'Real-time performance tracking',
        'Course engagement analytics',
        'Institutional metrics dashboard',
        'Custom report generation',
        'Data visualization tools'
      ]
    },
    {
      icon: BookOpen,
      title: 'Course Management',
      description: 'Create, organize, and deliver engaging courses with multimedia support, assessments, and interactive content.',
      details: [
        'Drag-and-drop course builder',
        'Multimedia content support',
        'Interactive quizzes and assessments',
        'Progress tracking',
        'Certificate generation'
      ]
    },
    {
      icon: Users,
      title: 'Student Management',
      description: 'Personalized learning experience with progress tracking, course materials, and communication tools.',
      details: [
        'Student enrollment management',
        'Progress tracking and analytics',
        'Personalized feedback system',
        'Group management',
        'Communication tools'
      ]
    },
    {
      icon: UserCheck,
      title: 'Teacher/Instructor Tools',
      description: 'Powerful tools for course creation, grading, student communication, and performance monitoring.',
      details: [
        'Course creation tools',
        'Automated grading system',
        'Student communication',
        'Performance monitoring',
        'Assessment creation'
      ]
    },
    {
      icon: Users,
      title: 'Parent Portal',
      description: 'Stay connected with your child\'s education through progress reports, communication channels, and academic insights.',
      details: [
        'Progress report access',
        'Communication with teachers',
        'Academic insights',
        'Attendance tracking',
        'Grade monitoring'
      ]
    },
    {
      icon: Settings,
      title: 'Management Tools',
      description: 'Administrative tools for user management, institutional reporting, and system configuration.',
      details: [
        'User management system',
        'Institutional reporting',
        'System configuration',
        'Role-based permissions',
        'Administrative controls & AI Surveillance'
      ]
    },
    {
      icon: UserCheck,
      title: 'Multi-role Support',
      description: 'Comprehensive support for students, teachers, parents, and management interfaces with role-based access.',
      details: [
        'Student interface',
        'Teacher interface',
        'Parent interface',
        'Management interface',
        'Role-based permissions'
      ]
    },
    {
      icon: Smartphone,
      title: 'Responsive Design',
      description: 'Works seamlessly across all devices with mobile-first design and touch-optimized interfaces.',
      details: [
        'Mobile-first design',
        'Touch-optimized interface',
        'Cross-device compatibility',
        'Progressive web app',
        'Offline capabilities'
      ]
    },
    {
      icon: Bell,
      title: 'Real-time Notifications',
      description: 'Stay updated with important alerts, announcements, and communication through instant notifications.',
      details: [
        'Instant alerts',
        'Announcement system',
        'Email notifications',
        'Mobile push notifications',
        'Custom notification settings'
      ]
    },
    {
      icon: FileText,
      title: 'Assessment & Testing',
      description: 'Built-in exam and quiz capabilities with advanced assessment tools and automated grading.',
      details: [
        'Exam creation tools',
        'Quiz generation',
        'Automated grading',
        'Plagiarism detection',
        'Assessment analytics'
      ]
    },
    {
      icon: BarChart3,
      title: 'Progress Tracking',
      description: 'Detailed analytics and reporting to monitor student progress and learning outcomes.',
      details: [
        'Student progress tracking',
        'Learning outcome monitoring',
        'Detailed analytics',
        'Performance reports',
        'Achievement tracking'
      ]
    },
    {
      icon: Shield,
      title: 'Secure Authentication',
      description: 'Protected routes and user management with enterprise-grade security and data protection.',
      details: [
        'Protected user routes',
        'User management system',
        'Data encryption',
        'Single sign-on (SSO)',
        'GDPR compliance'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Powerful Features for
              <span className="text-primary-600 block">Modern Learning</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Discover all the tools and features that make Lernix the preferred choice 
              for educational institutions worldwide.
            </p>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-xl mb-6">
                  <feature.icon size={32} className="text-primary-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {feature.title}
                </h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {feature.description}
                </p>
                <ul className="space-y-2">
                  {feature.details.map((detail, detailIndex) => (
                    <li key={detailIndex} className="flex items-center text-sm text-gray-600">
                      <div className="w-2 h-2 bg-primary-600 rounded-full mr-3"></div>
                      {detail}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-primary-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to explore these features?
          </h2>
          <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
            Start your free trial today and experience the full power of Lernix.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://lernix.ciphermind.tech"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 bg-white text-primary-600 hover:bg-gray-100 font-semibold rounded-lg transition-colors duration-200"
            >
              Live Demo
            </a>
            <Link
              to="/pricing"
              className="px-8 py-4 border-2 border-white text-white hover:bg-white hover:text-primary-600 font-semibold rounded-lg transition-colors duration-200"
            >
              Start Free Trial
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Features; 